import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, map, of, switchMap, withLatestFrom } from 'rxjs';
import * as StationActions from './station.actions';
import { selectStations, selectStationsQuery } from './station.selectors';
import { StationService } from '../services/station.service';
import { StationComponentService } from '../services/station-component.service';
import { StationComponentDto, StationDto } from '@shared/dto';
import { listResultActions$ } from '@shared/utils/pagination.utils';

@Injectable()
export class StationEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly stationService = inject(StationService);
  private readonly stationComponentService = inject(StationComponentService);

  readStations$ = createEffect(() => this.actions$.pipe(
    ofType(StationActions.readStations),
    exhaustMap(({ query }) =>
      listResultActions$(
        query ? this.stationService.read(undefined, { pagination: query }) : this.stationService.read(),
        query,
        ({ data, total }) => StationActions.readStationsSuccess({ stations: data, total }),
        (error) => StationActions.readStationsFailure({ error }),
      )),
  ));

  // Detail: resolve from the already-loaded list first (no HTTP), otherwise fetch
  // the single entity. The store is the source of truth that both the detail page
  // and the breadcrumb read from.
  readStation$ = createEffect(() => this.actions$.pipe(
    ofType(StationActions.readStation),
    withLatestFrom(this.store.select(selectStations)),
    switchMap(([{ stationId }, list]) => {
      const cached = list?.find((station) => station.id === stationId);
      if (cached) {
        return of(StationActions.readStationSuccess({ station: cached }));
      }

      return this.stationService.readOne({ stationId }).pipe(
        map((station) => StationActions.readStationSuccess({ station })),
        catchError((error) => of(StationActions.readStationFailure({ error }))),
      );
    }),
  ));

  createStation$ = createEffect(() => this.actions$.pipe(
    ofType(StationActions.createStation),
    exhaustMap(({ dto }) => this.stationService.create(dto).pipe(
      map((station: StationDto) => StationActions.createStationSuccess({ station })),
      catchError((error) => of(StationActions.createStationFailure({ error })))
    ))
  ));

  updateStation$ = createEffect(() => this.actions$.pipe(
    ofType(StationActions.updateStation),
    exhaustMap(({ stationId, dto }) => this.stationService.update({ stationId }, dto).pipe(
      map((station: Partial<StationDto>) => StationActions.updateStationSuccess({ station })),
      catchError((error) => of(StationActions.updateStationFailure({ error })))
    ))
  ));

  deleteStation$ = createEffect(() => this.actions$.pipe(
    ofType(StationActions.deleteStation),
    exhaustMap(({ stationId }) => this.stationService.delete({ stationId }).pipe(
      map((station: StationDto) => StationActions.deleteStationSuccess({ station })),
      catchError((error) => of(StationActions.deleteStationFailure({ error })))
    ))
  ));

  restoreStation$ = createEffect(() => this.actions$.pipe(
    ofType(StationActions.restoreStation),
    exhaustMap(({ stationId }) => this.stationService.restore({ stationId }).pipe(
      map((station: StationDto) => StationActions.restoreStationSuccess({ station })),
      catchError((error) => of(StationActions.restoreStationFailure({ error })))
    ))
  ));

  readStationComponents$ = createEffect(() => this.actions$.pipe(
    ofType(StationActions.readStationComponents),
    exhaustMap(() => this.stationComponentService.read().pipe(
      map((response) => {
        if (!Array.isArray(response)) {
          return StationActions.readStationComponentsFailure({ error: new Error('Expected plain component list') });
        }
        return StationActions.readStationComponentsSuccess({ components: response });
      }),
      catchError((error) => of(StationActions.readStationComponentsFailure({ error }))),
    ))
  ));

  // After a mutation, refetch the current page so counts/pagination stay correct.
  reloadAfterMutation$ = createEffect(() => this.actions$.pipe(
    ofType(
      StationActions.createStationSuccess,
      StationActions.updateStationSuccess,
      StationActions.deleteStationSuccess,
      StationActions.restoreStationSuccess,
    ),
    withLatestFrom(this.store.select(selectStationsQuery)),
    map(([, query]) => StationActions.readStations(query ?? undefined)),
  ));
}
